// ===========================================================================
// CALL DISPOSITION — AI-computed accuracy layer (marker: calls-disposition-v1)
// ---------------------------------------------------------------------------
// The carrier can NOT detect a machine-answer on an OUTBOUND call: RingCentral
// reports "Call connected" the instant the callee's line picks up, even when
// what actually answered was the voicemail GREETING and the caller hung up
// without leaving a message (prod row 39485: outbound, 51s, recording present,
// RC "Call connected", the green Connected chip, and a Grok summary that
// hallucinated "Leo left a voicemail"). This layer stores a SEPARATE, AI-derived
// disposition (persisted as a NEW nullable column, `ai_disposition`) that the
// display layer consults — it NEVER touches the RC `call_result` or the admin's
// manual `call_outcome`.
//
// This module is the pure, framework-agnostic FOUNDATION shared by:
//   • the display layer (src/lib/calls/callDisplay.ts — the ai_disposition tier
//     in deriveCallOutcome + the no-message branch in pickCallSubject),
//   • the calls view chip (src/views/calls/CallsList.tsx),
//   • the worker classifier/backfill (api/src/index.ts).
//
// The 6 disposition strings (the EXACT stored values):
//   live_conversation            — a real two-way conversation happened.
//   voicemail_left_by_us         — we reached the machine AND left a message.
//   reached_voicemail_no_message — we reached the machine and left NOTHING
//                                  (the row-39485 case: "Call connected" is a
//                                  lie; nobody talked to us).
//   voicemail_received           — an inbound voicemail was left FOR us.
//   no_speech_or_ivr             — no human speech / an IVR / dead air.
//   unknown                      — indeterminate; NO display override (the
//                                  existing carrier/manual/signal logic wins).
//
// Marker discipline: CALLS_DISPOSITION_MARKER is a RUNTIME string literal (not a
// comment) so it survives minification and is greppable in the served bundle /
// worker — the calls view stamps it on the chip data-attribute and the worker
// stamps it into the ai_disposition audit/ai_error payload.
// ===========================================================================

/** Minification-proof marker for this feature. Emitted at runtime by the view
 *  (chip data-attribute) and the worker (disposition audit/ai_error note). */
export const CALLS_DISPOSITION_MARKER = 'calls-disposition-v1'

/** The 6 valid disposition strings — the EXACT values stored in ai_disposition.
 *  Anything not in this set is garbage and must be treated as `unknown` (no
 *  display override). */
export const VALID_DISPOSITIONS: ReadonlySet<string> = new Set<string>([
  'live_conversation',
  'voicemail_left_by_us',
  'reached_voicemail_no_message',
  'voicemail_received',
  'no_speech_or_ivr',
  'unknown',
])

/**
 * Disposition → { OUTCOME category, chip label }. Consumed by the ai_disposition
 * tier in deriveCallOutcome (callDisplay.ts). `category` values are members of
 * `CallOutcomeCategory`; the tier resolves the chip color from
 * CALL_OUTCOME_CATEGORY_COLOR[category].
 *
 * DELIBERATELY there is NO `unknown` entry: an `unknown` (or absent, or garbage)
 * disposition must fall THROUGH to the existing carrier/manual/signal logic —
 * a hallucinated / indeterminate disposition may never override a decisive
 * carrier signal. `reached_voicemail_no_message` and `voicemail_left_by_us`
 * share the SAME blue `voicemail` tone but keep DISTINCT labels (Reached vs
 * Left) — a decided default.
 */
export const DISPOSITION_OUTCOME: Record<string, { category: string; label: string }> = {
  live_conversation: { category: 'connected', label: 'Connected' },
  voicemail_left_by_us: { category: 'voicemail', label: 'Left voicemail' },
  reached_voicemail_no_message: { category: 'voicemail', label: 'Reached voicemail' },
  voicemail_received: { category: 'voicemail', label: 'Voicemail' },
  no_speech_or_ivr: { category: 'other', label: 'No message' },
}

/** True for the two dispositions whose SUBJECT must be a system label instead of
 *  a (possibly hallucinated) ai_subject — see pickCallSubject in callDisplay.ts.
 *  Kept here so the view + worker can reuse the exact predicate. */
export function isNoMessageDisposition(disposition: string | null | undefined): boolean {
  const d = (disposition || '').trim()
  return d === 'reached_voicemail_no_message' || d === 'no_speech_or_ivr'
}
